import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { api } from "../../lib/api";
import { useSEO } from "../../lib/seo";
import {
  Headset, User, Code, MegaphoneSimple, PaintBrush, ChatCircleText,
  Table, Robot, Calculator, Lightning, ArrowRight, MagnifyingGlass, Globe, CurrencyDollar, ShieldCheck,
} from "@phosphor-icons/react";

const ICONS = { Headset, User, Code, MegaphoneSimple, PaintBrush, ChatCircleText, Table, Robot, Calculator, Lightning };

export default function VirtualLanding() {
  const [data, setData] = useState({ categories: [], service_types: [] });
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useSEO({
    title: "Hire Virtual Staff — Remote Workers, Freelancers & Agencies",
    description: "Hire pre-vetted virtual assistants, freelancers, web developers, designers, marketers and more — from anywhere in the world. Built for Canadian businesses.",
    path: "/virtual",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Virtual Staff Marketplace",
      provider: { "@type": "Organization", name: "CanHireSolution.com" },
      areaServed: "Worldwide",
    },
  });

  useEffect(() => {
    api.get("/virtual/categories").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    navigate(`/virtual/freelancers?q=${encodeURIComponent(q)}`);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* HERO */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden bg-brand text-white">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "32px 32px" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-12 gap-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="md:col-span-8">
            <div className="inline-flex items-center gap-2 border border-white/20 bg-white/10 px-3 py-1.5 text-xs uppercase tracking-[0.2em] font-semibold mb-6">
              <span className="w-1.5 h-1.5 bg-accent-red" /> New on CanHireSolution.com
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-6">
              Hire <span className="text-accent-red">virtual staff</span><br />from anywhere in the world.
            </h1>
            <p className="text-base sm:text-lg text-white/80 max-w-2xl mb-8">
              Virtual assistants, freelancers, agencies and "Get It Done For Me" specialists — vetted, rated, and ready to start. Built for Canadian businesses who need to move fast.
            </p>
            <form onSubmit={onSearch} className="flex items-stretch max-w-xl bg-white text-slate-900 border border-white/30 focus-within:border-accent-red" data-testid="virtual-search-form">
              <div className="pl-4 flex items-center text-slate-400"><MagnifyingGlass size={20} /></div>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="What do you need done? e.g. Shopify, logo, social media…"
                className="flex-1 px-3 py-4 text-base outline-none"
                data-testid="virtual-search-input"
              />
              <button type="submit" className="bg-accent-red text-white px-6 font-semibold hover:bg-[#D85A1A]" data-testid="virtual-search-btn">
                Search
              </button>
            </form>
            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-white/70">
              <span className="flex items-center gap-1.5"><Globe size={14} weight="bold" /> Hire from anywhere</span>
              <span className="flex items-center gap-1.5"><CurrencyDollar size={14} weight="bold" /> 10% platform fee on completion</span>
              <span className="flex items-center gap-1.5"><ShieldCheck size={14} weight="bold" /> Vetted profiles, real reviews</span>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="md:col-span-4 flex flex-col gap-3">
            <Link to="/virtual/jobs/new" className="block bg-white text-brand p-5 hover:-translate-y-1 transition-transform" data-testid="cta-post-job">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-accent-red mb-2">For Clients</div>
              <div className="font-display text-2xl mb-1">Post a project</div>
              <div className="text-sm text-slate-600">Describe what you need. Get applications within hours.</div>
              <div className="mt-3 inline-flex items-center gap-1 text-sm font-semibold">Post job <ArrowRight size={14} weight="bold" /></div>
            </Link>
            <Link to="/virtual/freelancer/profile" className="block bg-accent-red text-white p-5 hover:-translate-y-1 transition-transform" data-testid="cta-become-freelancer">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-white/80 mb-2">For Freelancers</div>
              <div className="font-display text-2xl mb-1">Get hired remotely</div>
              <div className="text-sm text-white/90">Build your profile. Apply to projects. Get paid.</div>
              <div className="mt-3 inline-flex items-center gap-1 text-sm font-semibold">Create profile <ArrowRight size={14} weight="bold" /></div>
            </Link>
            <div className="flex gap-2 text-xs">
              <Link to="/virtual/dashboard/client" className="flex-1 text-center border border-white/30 text-white px-3 py-2 hover:bg-white/10 font-semibold" data-testid="cta-client-dash">Client dashboard</Link>
              <Link to="/virtual/dashboard/freelancer" className="flex-1 text-center border border-white/30 text-white px-3 py-2 hover:bg-white/10 font-semibold" data-testid="cta-freelancer-dash">Freelancer dashboard</Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-accent-red mb-2">Browse by category</div>
          <h2 className="font-display text-3xl sm:text-4xl mb-10">What kind of help do you need?</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {data.categories.map((c) => {
              const Icon = ICONS[c.icon] || Lightning;
              return (
                <Link
                  key={c.id}
                  to={`/virtual/freelancers?category=${c.id}`}
                  className="bg-white border border-slate-200 p-5 hover:border-brand hover:-translate-y-1 transition-all"
                  data-testid={`virtual-cat-${c.id}`}
                >
                  <Icon size={28} weight="duotone" className="text-brand mb-3" />
                  <div className="font-display text-base leading-tight">{c.name}</div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-accent-red mb-2">How it works</div>
            <h2 className="font-display text-3xl sm:text-4xl mb-6">Hire help in three steps.</h2>
            <ol className="space-y-5">
              {[
                ["Post your project", "Describe the work, budget, and deadline. Free to post."],
                ["Review applications", "Pre-vetted freelancers apply with proposals and timelines."],
                ["Hire and chat", "Pick your match, open a project chat, and get to work."],
              ].map(([title, desc], i) => (
                <li key={title} className="flex gap-4">
                  <div className="flex-shrink-0 w-9 h-9 bg-brand text-white font-display flex items-center justify-center">{i + 1}</div>
                  <div>
                    <div className="font-display text-lg mb-1">{title}</div>
                    <div className="text-sm text-slate-600">{desc}</div>
                  </div>
                </li>
              ))}
            </ol>
            <Link to="/virtual/jobs/new" className="mt-8 inline-flex items-center gap-2 bg-brand text-white px-6 py-3 font-semibold hover:bg-[#122148]" data-testid="hiw-post-job-btn">
              Post your project <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
          <div className="bg-slate-50 border border-slate-200 p-8 lg:p-10">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-accent-red mb-2">For freelancers</div>
            <h3 className="font-display text-2xl mb-4">Get hired by Canadian businesses.</h3>
            <ul className="space-y-3 text-sm text-slate-700">
              <li className="flex items-start gap-2"><span className="text-brand mt-0.5">✓</span> Free profile, free to apply</li>
              <li className="flex items-start gap-2"><span className="text-brand mt-0.5">✓</span> Get paid securely (Stripe)</li>
              <li className="flex items-start gap-2"><span className="text-brand mt-0.5">✓</span> Build a reputation with real reviews</li>
              <li className="flex items-start gap-2"><span className="text-brand mt-0.5">✓</span> Browse open projects 24/7</li>
            </ul>
            <Link to="/virtual/freelancer/profile" className="mt-6 inline-flex items-center gap-2 bg-accent-red text-white px-6 py-3 font-semibold hover:bg-[#D85A1A]" data-testid="hiw-create-profile-btn">
              Create your profile <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
